import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin-wide config-health ribbon.
 * - Polls /api/admin/config-health once on mount
 * - Shows a red bar if any critical integration is broken (Perplexity / Resend / Twilio / Emergent LLM)
 * - Shows a yellow bar if only optional integrations are missing
 * - Silent if all green
 * - Dismissible for the session (sessionStorage key)
 */
export default function ConfigHealthRibbon() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("cor_health_ribbon_dismissed") === "1") setDismissed(true);
    } catch { /* private mode */ }
    api.get("/admin/config-health").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (dismissed || !data || data.overall === "green") return null;
  if (data.reds.length === 0 && data.yellows.length === 0 && data.pending_leads === 0) return null;

  const isRed = data.overall === "red" || data.pending_leads > 0;
  const bg = isRed ? "#DC2626" : "#D97706";
  const label = isRed ? "Config issue" : "Optional missing";
  const brokenNames = data.reds.map((r) => r.integration.split(" ")[0]).join(", ");
  const optionalNames = data.yellows.map((r) => r.integration.split(" ")[0]).join(", ");
  const parts = [];
  if (data.reds.length) parts.push(`${data.reds.length} broken (${brokenNames})`);
  if (data.pending_leads > 0) parts.push(`${data.pending_leads} recoverable lead${data.pending_leads === 1 ? "" : "s"} pending`);
  if (data.reds.length === 0 && data.yellows.length) parts.push(`${data.yellows.length} optional missing (${optionalNames})`);

  const dismiss = () => {
    try { sessionStorage.setItem("cor_health_ribbon_dismissed", "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      data-testid="admin-config-health-ribbon"
      style={{
        background: bg, color: "#fff", padding: "8px 40px 8px 12px",
        fontSize: 12, fontWeight: 600, position: "relative", display: "flex", alignItems: "center", gap: 8,
      }}
    >
      <AlertTriangle style={{ width: 14, height: 14 }} />
      <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1.5, opacity: 0.85, background: "rgba(255,255,255,0.18)", padding: "2px 8px", borderRadius: 3 }}>{label}</span>
      <span>{parts.join(" · ")}</span>
      <Link to="/settings/audit-console" style={{ color: "#fff", textDecoration: "underline", marginLeft: "auto", marginRight: 24 }}>Fix →</Link>
      <button onClick={dismiss} aria-label="Dismiss" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", color: "#fff", border: "none", borderRadius: 3, width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <X style={{ width: 12, height: 12 }} />
      </button>
    </div>
  );
}
